/**
 * Tenant Context for Subdomain-based Multi-tenancy
 * 
 * Detects tenant from:
 * 1. URL query parameter (?tenant=oldmutual) - for preview/testing
 * 2. Subdomain (oldmutual.cerebro.co.za) - for production
 * 
 * Provides:
 * - Tenant identification
 * - Branding configuration
 * - Feature flags
 * - SSO configuration
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../config/api';

const API = API_BASE;

// Session storage key for tenant override
const TENANT_OVERRIDE_KEY = 'cerebro_tenant_override';

// Master platform defaults
const MASTER_TENANT = {
  isMaster: true,
  slug: null,
  orgId: null,
  name: 'Cerebro Core',
  branding: {
    logoUrl: 'https://customer-assets.emergentagent.com/job_cpd-compass/artifacts/km6w43n1_Teal%20Cerebro%20Transp.png',
    primaryColor: '#D4AF37',
    poweredByVisible: false
  },
  features: {
    courses: true,
    learning_labs: true,
    learning_pathways: true,
    masterclasses: true,
    ebooks: true,
    wsp: true,
    certificates: true,
    navi_coach: true,
    ai_course_generation: true,
    cpd_tracking: true,
    external_courses: true  // External content library (Old Mutual, etc.)
  },
  sso: {
    enabled: false,
    provider: null,
    loginUrl: null
  }
};

const TenantContext = createContext({
  tenant: MASTER_TENANT,
  loading: true,
  error: null,
  isTenant: false,
  tenantSlug: null,
  setTenantOverride: () => {},
  clearTenantOverride: () => {},
  refreshTenant: async () => {}
});

/**
 * Get tenant slug from URL query parameter
 */
function getTenantFromQuery() {
  const params = new URLSearchParams(window.location.search);
  return params.get('tenant');
}

/**
 * Get tenant slug from session storage (persists during navigation)
 */
function getTenantFromStorage() {
  try {
    return sessionStorage.getItem(TENANT_OVERRIDE_KEY);
  } catch {
    return null;
  }
}

/**
 * Save tenant slug to session storage
 */
function saveTenantToStorage(slug) {
  try {
    if (slug) {
      sessionStorage.setItem(TENANT_OVERRIDE_KEY, slug);
    } else {
      sessionStorage.removeItem(TENANT_OVERRIDE_KEY);
    }
  } catch {
    // Ignore storage errors
  }
}

/**
 * Extract tenant slug from current hostname
 */
function extractTenantSlug() {
  const hostname = window.location.hostname.toLowerCase();
  
  // Master domains (no tenant)
  const masterDomains = [
    'localhost',
    '127.0.0.1',
    'cerebro.co.za',
    'www.cerebro.co.za',
    'admin.cerebro.co.za'
  ];
  
  // Check for preview environment
  if (hostname.includes('preview.emergentagent.com')) {
    // Check for tenant prefix with double-hyphen: tenant--appname.preview.emergentagent.com
    const parts = hostname.split('.');
    if (parts.length > 0 && parts[0].includes('--')) {
      const potentialSlug = parts[0].split('--')[0];
      const reserved = ['www', 'admin', 'api', 'app', 'cpd', 'dev', 'test'];
      if (!reserved.includes(potentialSlug)) {
        return potentialSlug;
      }
    }
    return null;
  }
  
  // Check if master domain
  if (masterDomains.includes(hostname)) {
    return null;
  }
  
  // Extract subdomain
  const parts = hostname.split('.');
  if (parts.length >= 3) {
    const subdomain = parts[0];
    const reserved = ['www', 'admin', 'api', 'app', 'mail', 'cdn', 'dev', 'staging', 'test', 'qa'];
    if (!reserved.includes(subdomain)) {
      return subdomain;
    }
  }
  
  return null;
}

/**
 * Determine tenant slug from all sources (priority order)
 */
function determineTenantSlug() {
  // 1. Check URL query parameter (highest priority - for testing/preview)
  const queryTenant = getTenantFromQuery();
  if (queryTenant) {
    saveTenantToStorage(queryTenant);
    return queryTenant;
  }
  
  // 2. Check session storage (persists during navigation)
  const storedTenant = getTenantFromStorage();
  if (storedTenant) {
    return storedTenant;
  }
  
  // 3. Check subdomain (production)
  return extractTenantSlug();
}

export function TenantProvider({ children }) {
  const [tenant, setTenant] = useState(MASTER_TENANT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tenantSlug, setTenantSlug] = useState(null);

  const fetchTenant = async (slugOverride = null) => {
    setLoading(true);
    setError(null);
    
    // Determine which tenant to load (priority: override > query > storage > subdomain)
    const targetSlug = slugOverride || determineTenantSlug();
    setTenantSlug(targetSlug);
    
    if (!targetSlug) {
      // No tenant specified - use master platform
      setTenant(MASTER_TENANT);
      setLoading(false);
      return;
    }
    
    try {
      // Fetch tenant by slug
      const res = await axios.get(`${API}/api/tenant/by-slug/${targetSlug}`);
      const data = res.data;
      
      setTenant({
        isMaster: false,
        slug: data.slug,
        orgId: data.org_id,
        name: data.name,
        branding: {
          logoUrl: data.branding?.logo_url,
          primaryColor: data.branding?.primary_color || '#D4AF37',
          faviconUrl: data.branding?.favicon_url,
          loginBackgroundUrl: data.branding?.login_background_url,
          poweredByVisible: true
        },
        features: data.features || MASTER_TENANT.features,
        sso: {
          enabled: data.sso?.enabled || false,
          provider: data.sso?.provider,
          loginUrl: data.sso?.login_url
        }
      });
      
      // Save to storage for persistence during navigation
      saveTenantToStorage(targetSlug);
      
    } catch (err) {
      console.error('Failed to fetch tenant:', err);
      setError(`Tenant '${targetSlug}' not found`);
      setTenant(MASTER_TENANT);
      // Clear invalid tenant from storage
      saveTenantToStorage(null);
    } finally {
      setLoading(false);
    }
  };

  // Set tenant override manually (for admin switching)
  const setTenantOverride = (slug) => {
    saveTenantToStorage(slug);
    fetchTenant(slug);
  };

  // Clear tenant override (return to master)
  const clearTenantOverride = () => {
    saveTenantToStorage(null);
    setTenantSlug(null);
    setTenant(MASTER_TENANT);
  };

  useEffect(() => {
    fetchTenant();
  }, []);

  // Apply tenant branding to CSS variables
  useEffect(() => {
    if (tenant.branding?.primaryColor) {
      const primary = tenant.branding.primaryColor;
      document.documentElement.style.setProperty('--tenant-primary', primary);
      
      // Also set derived colors for consistent theming
      document.documentElement.style.setProperty('--tenant-primary-hover', adjustBrightness(primary, -15));
      document.documentElement.style.setProperty('--tenant-primary-light', adjustBrightness(primary, 30));
    }
    
    // Update document title
    if (!tenant.isMaster && tenant.name) {
      document.title = `${tenant.name} | Learning Platform`;
    } else {
      document.title = 'Cerebro Core | A Workforce Capability Platform';
    }
    
    // Update favicon if provided
    if (tenant.branding?.faviconUrl) {
      const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
      link.rel = 'icon';
      link.href = tenant.branding.faviconUrl;
      document.head.appendChild(link);
    }
  }, [tenant]);

  return (
    <TenantContext.Provider value={{
      tenant,
      loading,
      error,
      isTenant: !tenant.isMaster,
      tenantSlug,
      setTenantOverride,
      clearTenantOverride,
      refreshTenant: fetchTenant
    }}>
      {children}
    </TenantContext.Provider>
  );
}

// Helper: Adjust color brightness for derived colors
function adjustBrightness(hex, percent) {
  if (!hex) return hex;
  hex = hex.replace('#', '');
  
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  const adjust = (val) => Math.max(0, Math.min(255, val + Math.round((percent / 100) * 255)));
  
  const newR = adjust(r).toString(16).padStart(2, '0');
  const newG = adjust(g).toString(16).padStart(2, '0');
  const newB = adjust(b).toString(16).padStart(2, '0');
  
  return `#${newR}${newG}${newB}`;
}

export function useTenant() {
  return useContext(TenantContext);
}

/**
 * Hook to check if a feature is enabled for the current tenant
 */
export function useTenantFeature(featureName) {
  const { tenant } = useTenant();
  return tenant.features?.[featureName] !== false;
}

/**
 * Component that only renders children if feature is enabled
 */
export function TenantFeature({ feature, children, fallback = null }) {
  const isEnabled = useTenantFeature(feature);
  return isEnabled ? children : fallback;
}

export default TenantContext;
